import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n.jsx";
import { PageHeader, StatusPill, Empty } from "../components/Bits";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Archive, Search, CheckSquare, KeyRound, Shirt } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

const BLANK = { first_name: "", last_name: "", email: "", phone: "", password: "", role_ids: [], position_id: "", team_id: "", project_ids: [], hire_date: "", hourly_rate: 0 };

export default function Employees() {
  const { t, tn } = useI18n();
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [positions, setPositions] = useState([]);
  const [teams, setTeams] = useState([]);
  const [q, setQ] = useState("");
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [editId, setEditId] = useState(null);
  const [sel, setSel] = useState([]);

  const nav = useNavigate(); const loc = useLocation();
  const load = () => api.get("/employees").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/roles").then((r) => setRoles(r.data));
    api.get("/config/positions").then((r) => setPositions(r.data));
    api.get("/config/teams").then((r) => setTeams(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);
  useEffect(() => { const id = loc.state?.edit; if (id && rows.length) { const r = rows.find((x) => x.id === id); if (r) { open(r); window.history.replaceState({}, ""); } } }, [loc.state, rows]); // eslint-disable-line
  const [projects, setProjects] = useState([]);
  const toggleProject = (id) => setForm((f) => ({ ...f, project_ids: (f.project_ids || []).includes(id) ? f.project_ids.filter((x) => x !== id) : [...(f.project_ids || []), id] }));

  const open = (row) => {
    if (row) { setEditId(row.id); setForm({ ...BLANK, ...row, password: "" }); }
    else { setEditId(null); setForm(BLANK); }
    setModal(true);
  };
  const [revealed, setRevealed] = useState(null);
  const genPassword = () => {
    const sets = ["ABCDEFGHJKLMNPQRSTUVWXYZ", "abcdefghijkmnopqrstuvwxyz", "23456789", "!@#$%&*?"];
    const all = sets.join(""); const arr = new Uint32Array(12); crypto.getRandomValues(arr);
    let pw = sets.map((s, i) => s[arr[i] % s.length]).join("");
    for (let i = 4; i < 12; i++) pw += all[arr[i] % all.length];
    return pw.split("").sort(() => (crypto.getRandomValues(new Uint8Array(1))[0] % 2) - 0.5).join("");
  };
  const save = async () => {
    try {
      if (editId) {
        await api.put(`/employees/${editId}`, form);
        if (form.password) setRevealed({ email: form.email, password: form.password });
      } else {
        const { data } = await api.post("/employees", form);
        if (data.temp_password) setRevealed({ email: data.email, password: data.temp_password });
      }
      toast.success(form.password && editId ? `${t("save")} · ${t("password_changed")}` : t("save")); setModal(false); load();
    } catch (e) { const d = e.response?.data?.detail; toast.error(t(d) !== d ? t(d) : formatApiError(d)); }
  };
  const resetPw = async () => {
    try { const { data } = await api.post(`/employees/${editId}/reset-password`, {}); setRevealed({ email: form.email, password: data.password }); setModal(false); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const archive = async (id) => { try { await api.delete(`/employees/${id}`); toast.success(t("archive")); load(); } catch (e) { const d = e.response?.data?.detail; toast.error(t(d) !== d ? t(d) : formatApiError(d)); } };
  const bulk = async (action, value) => {
    await api.post("/employees/bulk", { ids: sel, action, value });
    toast.success(`${action} · ${sel.length}`); setSel([]); load();
  };

  const filtered = rows.filter((r) => (r.name + r.email).toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader title={t("employees")} subtitle={`${rows.length}`}
        action={<div className="flex gap-2">
          <Button variant="outline" onClick={() => nav("/employees/sizes")} className="gap-1" data-testid="sizes-report-btn"><Shirt size={16} /> <span className="hidden sm:inline">{t("workwear_sizes")}</span></Button>
          {can("employees.create") && (
          <Button data-testid="add-employee-btn" onClick={() => open(null)} className="bg-[var(--amber)] hover:bg-[var(--amber-2)] text-white gap-2">
            <Plus size={16} /> {t("create")}
          </Button>
        )}</div>} />

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="employee-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")} className="pl-9" />
        </div>
        {sel.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-[var(--text-3)]">{sel.length}</span>
            <select data-testid="bulk-role" onChange={(e) => e.target.value && bulk("set_role", e.target.value)} className="h-9 border rounded-md px-2 text-sm">
              <option value="">{t("role")}…</option>
              {roles.map((r) => <option key={r.id} value={r.id}>{tn(r.name)}</option>)}
            </select>
            <Button variant="outline" size="sm" data-testid="bulk-archive" onClick={() => bulk("archive")}><Archive size={14} /></Button>
          </div>
        )}
      </div>

      {filtered.length === 0 ? <Empty /> : (<>
        <div className="md:hidden space-y-2" data-testid="employees-mobile-list">
          {filtered.map((r) => (
            <div key={r.id} data-testid={`emp-card-${r.id}`} className="tactical-card p-3 flex items-center gap-3">
              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => nav(`/employees/${r.id}`)} data-testid={`emp-open-${r.id}`}>
                <div className="font-semibold truncate">{r.name}</div>
                <div className="text-xs text-[var(--text-3)] truncate">{r.role_ids?.map((id) => tn(roles.find((x) => x.id === id)?.name)).filter(Boolean).join(", ") || "—"}</div>
              </div>
              <StatusPill status={r.archived ? "blocked" : "active"} />
              {can("employees.edit") && <button onClick={() => open(r)} className="text-[var(--blue)] text-sm font-semibold p-2 -m-2">{t("edit")}</button>}
            </div>
          ))}
        </div>
        <div className="hidden md:block tactical-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border)] text-left text-[var(--text-3)]">
                <th className="p-3"><CheckSquare size={14} /></th>
                <th className="p-3">{t("name")}</th>
                <th className="p-3 hidden sm:table-cell">{t("email")}</th>
                <th className="p-3">{t("role")}</th>
                <th className="p-3 hidden md:table-cell">{t("position")}</th>
                <th className="p-3">{t("status")}</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} data-testid={`emp-row-${r.id}`} className="border-b border-[var(--border)] hover:bg-[var(--bg)]">
                  <td className="p-3"><input type="checkbox" checked={sel.includes(r.id)}
                    onChange={(e) => setSel((s) => e.target.checked ? [...s, r.id] : s.filter((x) => x !== r.id))} data-testid={`emp-check-${r.id}`} /></td>
                  <td className="p-3 font-semibold cursor-pointer text-[var(--blue)]" onClick={() => nav(`/employees/${r.id}`)} data-testid={`emp-open-${r.id}`}>{r.name}</td>
                  <td className="p-3 hidden sm:table-cell text-[var(--text-3)]">{r.email}</td>
                  <td className="p-3">{r.role_ids?.map((id) => tn(roles.find((x) => x.id === id)?.name)).filter(Boolean).join(", ") || "—"}</td>
                  <td className="p-3 hidden md:table-cell">{tn(positions.find((p) => p.id === r.position_id)?.name) || "—"}</td>
                  <td className="p-3"><StatusPill status={r.archived ? "blocked" : "active"} /></td>
                  <td className="p-3 text-right whitespace-nowrap">
                    {can("employees.edit") && <button onClick={() => open(r)} className="text-[var(--blue)] mr-3" data-testid={`emp-edit-${r.id}`}>{t("edit")}</button>}
                    {can("employees.archive") && !r.archived && <button onClick={() => archive(r.id)} className="text-[var(--red)]" data-testid={`emp-archive-${r.id}`}><Archive size={15} /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>)}

      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editId ? t("edit") : t("create")} — {t("employees")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder={t("first_name")} value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="emp-first" />
            <Input placeholder={t("last_name")} value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="emp-last" />
            <Input placeholder={t("email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="emp-email" disabled={!!editId} />
            <Input placeholder={t("phone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="emp-phone" />
            {can("users.manage") ? (
              <div className="flex gap-1">
                <Input type="text" placeholder={editId ? t("password_keep_hint") : t("password")} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="emp-password" />
                <Button type="button" variant="outline" size="sm" className="h-10 px-2 shrink-0" onClick={() => setForm({ ...form, password: genPassword() })} data-testid="emp-gen-password" title={t("generate")}><KeyRound size={14} /></Button>
              </div>
            ) : <div className="text-xs text-[var(--text-3)] flex items-center">{t("password_admin_only")}</div>}
            <Input type="date" value={form.hire_date} onChange={(e) => setForm({ ...form, hire_date: e.target.value })} data-testid="emp-hire" />
            <select className="border rounded-md h-10 px-2 text-sm" value={form.role_ids?.[0] || ""} onChange={(e) => setForm({ ...form, role_ids: [e.target.value] })} data-testid="emp-role">
              <option value="">{t("role")}…</option>
              {roles.map((r) => <option key={r.id} value={r.id}>{tn(r.name)}</option>)}
            </select>
            <select className="border rounded-md h-10 px-2 text-sm" value={form.position_id || ""} onChange={(e) => setForm({ ...form, position_id: e.target.value })} data-testid="emp-position">
              <option value="">{t("position")}…</option>
              {positions.map((p) => <option key={p.id} value={p.id}>{tn(p.name)}</option>)}
            </select>
            <select className="border rounded-md h-10 px-2 text-sm" value={form.team_id || ""} onChange={(e) => setForm({ ...form, team_id: e.target.value })} data-testid="emp-team">
              <option value="">{t("team")}…</option>
              {teams.map((tm) => <option key={tm.id} value={tm.id}>{tn(tm.name)}</option>)}
            </select>
          </div>
          <div className="mt-3">
            <div className="text-xs uppercase font-semibold text-[var(--text-3)] mb-1">{t("projects")} ({(form.project_ids || []).length})</div>
            <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto" data-testid="emp-projects">
              {projects.map((p) => <button key={p.id} type="button" data-testid={`emp-project-${p.id}`} onClick={() => toggleProject(p.id)} className={`px-3 h-8 rounded-full text-xs font-semibold ${(form.project_ids || []).includes(p.id) ? "bg-[var(--amber)] text-white" : "tactical-card"}`}>{p.name}</button>)}
            </div>
          </div>
          {editId && form.history?.length > 0 && (
            <div className="text-xs text-[var(--text-3)] border-t pt-2 mt-1">
              {form.history.slice(-3).map((h, i) => <div key={i}>{(h.date || "").slice(0, 10)} — {h.change}</div>)}
            </div>
          )}
          <DialogFooter>
            {editId && can("users.manage") && <Button variant="outline" onClick={resetPw} className="mr-auto gap-1 text-[var(--red)]" data-testid="emp-reset-password"><KeyRound size={14} /> {t("reset_password")}</Button>}
            <Button variant="outline" onClick={() => setModal(false)}>{t("cancel")}</Button>
            <Button onClick={save} className="bg-[var(--amber)] hover:bg-[var(--amber-2)] text-white" data-testid="emp-save">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!revealed} onOpenChange={() => setRevealed(null)}>
        <DialogContent className="max-w-md" data-testid="password-reveal">
          <DialogHeader><DialogTitle>{t("new_password_title")}</DialogTitle></DialogHeader>
          <div className="text-sm text-[var(--text-3)]">{revealed?.email}</div>
          <div className="font-mono-num text-2xl font-bold tracking-wider tactical-card p-3 text-center select-all" data-testid="revealed-password">{revealed?.password}</div>
          <p className="text-xs text-[var(--text-3)]">{t("password_once")}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => { navigator.clipboard?.writeText(revealed.password); toast.success(t("copied")); }} data-testid="copy-password">{t("copy")}</Button>
            <Button onClick={() => setRevealed(null)} className="bg-[var(--amber)] text-white">OK</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
